<template>
  <div class="page-outer">
    <!-- 固定頂部 header -->
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">時段控制設定</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleSave" :disabled="submitting">
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="form-container">
      <form @submit.prevent="handleSave">
        <!-- 基本設定 -->
        <div class="form-section">
          <h3 class="section-title">基本設定</h3>

          <div class="form-group">
            <label>設定名稱</label>
            <input
              v-model="formData.configName"
              type="text"
              class="form-control"
              placeholder="例如：總店營業時間"
              maxlength="100"
            />
          </div>

          <div class="form-group">
            <label>時區</label>
            <select v-model="formData.timezone" class="form-control">
              <option value="Asia/Taipei">Asia/Taipei (台灣)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (日本)</option>
              <option value="Asia/Shanghai">Asia/Shanghai (中國)</option>
              <option value="Asia/Hong_Kong">Asia/Hong_Kong (香港)</option>
            </select>
          </div>

          <div class="form-group">
            <label>
              <input
                v-model="formData.isActive"
                type="checkbox"
              />
              啟用時段控制
            </label>
            <small class="form-text">啟用後，非營業時間將自動回覆設定的範本訊息</small>
          </div>
        </div>

        <!-- 營業時間設定 -->
        <div class="form-section">
          <h3 class="section-title">營業時間設定</h3>
          <p class="section-description">設定每週的營業時間</p>

          <div class="hours-table">
            <table>
              <thead>
                <tr>
                  <th>星期</th>
                  <th>是否營業</th>
                  <th>開門時間</th>
                  <th>關門時間</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(day, index) in days" :key="index">
                  <td class="day-name">{{ day.label }}</td>
                  <td>
                    <label class="switch">
                      <input
                        v-model="formData.businessHours[day.value].enabled"
                        type="checkbox"
                        @change="handleDayToggle(day.value)"
                      />
                      <span class="slider"></span>
                    </label>
                  </td>
                  <td>
                    <input
                      v-model="formData.businessHours[day.value].open"
                      type="time"
                      class="form-control time-input"
                      :disabled="!formData.businessHours[day.value].enabled"
                      pattern="^\\d{2}:\\d{2}$"
                    />
                  </td>
                  <td>
                    <input
                      v-model="formData.businessHours[day.value].close"
                      type="time"
                      class="form-control time-input"
                      :disabled="!formData.businessHours[day.value].enabled"
                      pattern="^\\d{2}:\\d{2}$"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 休假日設定 -->
        <div class="form-section">
          <h3 class="section-title">休假日設定</h3>
          <p class="section-description">設定特殊休假日（例如：國定假日、店休日）</p>

          <div class="holidays-input">
            <div
              v-for="(holiday, index) in formData.holidays"
              :key="index"
              class="holiday-item"
            >
              <input
                v-model="formData.holidays[index]"
                type="date"
                class="form-control"
              />
              <button
                type="button"
                class="btn btn-sm btn-danger"
                @click="removeHoliday(index)"
              >
                <i class="ri-close-line"></i>
              </button>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            @click="addHoliday"
          >
            <i class="ri-add-line"></i>
            新增休假日
          </button>
        </div>

        <!-- 非營業時間回覆設定 -->
        <div class="form-section">
          <h3 class="section-title">非營業時間回覆設定</h3>

          <div class="form-group">
            <label>
              <input
                v-model="formData.enableKeywordMatchInNonBusinessHours"
                type="checkbox"
              />
              非營業時間也匹配關鍵字規則
            </label>
            <small class="form-text">
              啟用後，非營業時間也會先嘗試匹配關鍵字規則。如果匹配到規則，會發送規則回覆；如果沒有匹配到規則，才會發送非營業時間提示。
              停用後，非營業時間只會發送非營業時間提示，不會匹配關鍵字規則。
            </small>
          </div>

          <div class="form-group">
            <label>非營業時間回覆範本代碼</label>
            <input
              v-model="formData.nonBusinessHourReplyTemplateCode"
              type="text"
              class="form-control"
              placeholder="請輸入 LINE 訊息範本代碼"
              maxlength="50"
            />
            <small class="form-text">
              對應 line_message_templates.template_code。
              <span v-if="formData.enableKeywordMatchInNonBusinessHours">
                當用戶在非營業時間發送訊息且未匹配到關鍵字規則時，系統會自動回覆此範本。
              </span>
              <span v-else>
                當用戶在非營業時間發送訊息時，系統會自動回覆此範本。
              </span>
            </small>
          </div>

          <div class="preview-section">
            <h4>當前時間預覽</h4>
            <div class="preview-info">
              <p><strong>當前時間：</strong>{{ currentTime }}</p>
              <p><strong>當前星期：</strong>{{ currentDayName }}</p>
              <p><strong>是否在營業時間內：</strong>
                <span :class="isBusinessHoursNow ? 'text-success' : 'text-danger'">
                  {{ isBusinessHoursNow ? '是' : '否' }}
                </span>
              </p>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { autoReplyService } from '@/api/modules/autoReply.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'AutoReplyBusinessHours',
  data() {
    return {
      loading: false,
      submitting: false,
      days: [
        { label: '週一', value: 'monday' },
        { label: '週二', value: 'tuesday' },
        { label: '週三', value: 'wednesday' },
        { label: '週四', value: 'thursday' },
        { label: '週五', value: 'friday' },
        { label: '週六', value: 'saturday' },
        { label: '週日', value: 'sunday' }
      ],
      formData: {
        configName: '營業時間設定',
        timezone: 'Asia/Taipei',
        businessHours: {
          monday: { enabled: true, open: '09:00', close: '18:00' },
          tuesday: { enabled: true, open: '09:00', close: '18:00' },
          wednesday: { enabled: true, open: '09:00', close: '18:00' },
          thursday: { enabled: true, open: '09:00', close: '18:00' },
          friday: { enabled: true, open: '09:00', close: '18:00' },
          saturday: { enabled: false, open: '09:00', close: '18:00' },
          sunday: { enabled: false, open: '09:00', close: '18:00' }
        },
        holidays: [],
        nonBusinessHourReplyTemplateCode: '',
        enableKeywordMatchInNonBusinessHours: false,
        isActive: true
      },
      currentTime: '',
      currentDayName: '',
      isBusinessHoursNow: false
    };
  },
  mounted() {
    this.loadConfig();
    this.updateCurrentTime();
    // 每秒更新當前時間
    setInterval(() => {
      this.updateCurrentTime();
    }, 1000);
  },
  methods: {
    async loadConfig() {
      this.loading = true;
      try {
        const response = await autoReplyService.getBusinessHours();
        if (response.data) {
          const config = response.data;
          this.formData = {
            configName: config.configName || '營業時間設定',
            timezone: config.timezone || 'Asia/Taipei',
            businessHours: config.businessHours || this.getDefaultBusinessHours(),
            holidays: config.holidays || [],
            nonBusinessHourReplyTemplateCode: config.nonBusinessHourReplyTemplateCode || '',
            enableKeywordMatchInNonBusinessHours: config.enableKeywordMatchInNonBusinessHours !== undefined ? config.enableKeywordMatchInNonBusinessHours : false,
            isActive: config.isActive !== undefined ? config.isActive : true
          };
        }
      } catch (error) {
        console.error('載入設定失敗:', error);
        // 如果沒有設定，使用預設值
      } finally {
        this.loading = false;
      }
    },

    getDefaultBusinessHours() {
      return {
        monday: { enabled: true, open: '09:00', close: '18:00' },
        tuesday: { enabled: true, open: '09:00', close: '18:00' },
        wednesday: { enabled: true, open: '09:00', close: '18:00' },
        thursday: { enabled: true, open: '09:00', close: '18:00' },
        friday: { enabled: true, open: '09:00', close: '18:00' },
        saturday: { enabled: false, open: '09:00', close: '18:00' },
        sunday: { enabled: false, open: '09:00', close: '18:00' }
      };
    },

    async handleSave() {
      this.submitting = true;
      try {
        await autoReplyService.updateBusinessHours(this.formData);
        await showSuccess('儲存成功', '');
      } catch (error) {
        console.error('儲存失敗:', error);
        showError('儲存失敗', error.response?.data?.message || '');
      } finally {
        this.submitting = false;
      }
    },

    handleDayToggle(dayValue) {
      // 當啟用某天時，設定預設時間
      if (this.formData.businessHours[dayValue].enabled) {
        if (!this.formData.businessHours[dayValue].open) {
          this.formData.businessHours[dayValue].open = '09:00';
        }
        if (!this.formData.businessHours[dayValue].close) {
          this.formData.businessHours[dayValue].close = '18:00';
        }
      }
    },

    addHoliday() {
      this.formData.holidays.push('');
    },

    removeHoliday(index) {
      this.formData.holidays.splice(index, 1);
    },

    updateCurrentTime() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString('zh-TW', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: this.formData.timezone
      });

      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayIndex = now.getDay();
      const dayName = dayNames[dayIndex];
      this.currentDayName = this.days.find(d => d.value === dayName)?.label || dayName;

      // 檢查是否在營業時間內
      this.checkBusinessHours(now, dayName);
    },

    checkBusinessHours(now, dayName) {
      const dayConfig = this.formData.businessHours[dayName];
      if (!dayConfig || !dayConfig.enabled) {
        this.isBusinessHoursNow = false;
        return;
      }

      // 檢查是否為休假日
      const dateStr = now.toISOString().split('T')[0];
      if (this.formData.holidays.includes(dateStr)) {
        this.isBusinessHoursNow = false;
        return;
      }

      // 檢查時間
      const currentTime = now.toTimeString().slice(0, 5); // HH:mm
      const openTime = dayConfig.open || '00:00';
      const closeTime = dayConfig.close || '23:59';

      this.isBusinessHoursNow = currentTime >= openTime && currentTime <= closeTime;
    }
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.section-description {
  color: #666;
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.hours-table {
  overflow-x: auto;
}

.hours-table table {
  width: 100%;
  border-collapse: collapse;
}

.hours-table th,
.hours-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.hours-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.day-name {
  font-weight: 500;
}

.time-input {
  width: 120px;
}

.holidays-input {
  margin-bottom: 12px;
}

.holiday-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.holiday-item .form-control {
  flex: 1;
  max-width: 200px;
}

.preview-section {
  margin-top: 24px;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.preview-section h4 {
  margin-top: 0;
  margin-bottom: 12px;
}

.preview-info p {
  margin: 8px 0;
}

.text-success {
  color: #4caf50;
  font-weight: 600;
}

.text-danger {
  color: #f44336;
  font-weight: 600;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #2196f3;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.spin { animation: spin-btn 0.8s linear infinite; }
@keyframes spin-btn { to { transform: rotate(360deg); } }

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
